// src/components/LoanCalculationForms/Schedule.tsx
import React, { useMemo } from "react";
import { formatDayjsDate } from "@/utils/dateUtils";

// shadcn
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

// icons
import { CalendarClock, CheckCircle2 } from "lucide-react";
import {LoanScheduleItem} from "@/utils/loanCalculation";

interface ScheduleProps {
    showSchedule: boolean;
    schedule: LoanScheduleItem[];
    earlyClosed: boolean;
    status: string;
    canEarlyClose: any;
    onEarlyClose: () => void;
}

const formatSom = (v: number) =>
    new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "KGS",
        minimumFractionDigits: 0,
    }).format(v);

const Schedule: React.FC<ScheduleProps> = ({
                                               showSchedule,
                                               schedule,
                                               earlyClosed,
                                               status,
                                               canEarlyClose,
                                               onEarlyClose,
                                           }) => {
    const hidden = useMemo(() => {
        return (
            !showSchedule ||
            schedule.length === 0 ||
            ["closedEarly", "returned", "closed"].includes(status) ||
            earlyClosed
        );
    }, [showSchedule, schedule.length, status, earlyClosed]);

    if (hidden) return null;

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <CalendarClock className="h-5 w-5 text-muted-foreground" />
                        <CardTitle className="text-base sm:text-lg">График выплат</CardTitle>
                    </div>

                    {!!canEarlyClose && (
                        <Button
                            type="button"
                            variant="default"
                            onClick={onEarlyClose}
                            className="h-9 w-full sm:w-auto"
                        >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Закрыть кредит
                        </Button>
                    )}
                </div>
            </CardHeader>

            <CardContent className="space-y-4">
                <Separator />

                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[90px]">Месяц</TableHead>
                                <TableHead className="min-w-[160px]">Дата платежа</TableHead>
                                <TableHead className="text-right min-w-[170px]">
                                    Ежемесячный платеж
                                </TableHead>
                                <TableHead className="text-right min-w-[140px]">Остаток</TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {schedule.map((item) => (
                                <TableRow key={item.month}>
                                    <TableCell className="font-medium">{item.month}</TableCell>
                                    <TableCell>{formatDayjsDate(item.date)}</TableCell>
                                    <TableCell className="text-right">
                                        {formatSom(Number(item.monthlyPayment || 0))}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {formatSom(Number(item.remaining || 0))}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
};

export default Schedule;
